import React, { useState } from "react";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import SwipeableDrawer from "@material-ui/core/SwipeableDrawer";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import FilterListIcon from "@material-ui/icons/FilterList";
import CloseIcon from "@material-ui/icons/Close";

import { i18n } from "../../translate/i18n";
import TaskUserAutocomplete from "../../components/TaskUserAutocomplete";
import TaskContactAutocomplete from "../../components/TaskContactAutocomplete";
import { TASK_STATUS_OPTIONS, TASK_PRIORITY_OPTIONS, TASK_TYPE_OPTIONS } from "./taskUtils";

const useStyles = makeStyles(theme => ({
  desktopFilters: {
    marginBottom: theme.spacing(1)
  },
  drawerContent: {
    padding: theme.spacing(2),
    // O painel deslizante nunca deve exigir rolagem horizontal — todo
    // campo ocupa a largura total disponível.
    width: "100%",
    boxSizing: "border-box"
  },
  drawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2)
  },
  fieldSpacing: {
    marginBottom: theme.spacing(2)
  }
}));

// Painel de filtros — linha inline no desktop, SwipeableDrawer inferior no
// celular (fecha pelo botão, pelo gesto de arrastar para baixo, ou
// clicando fora — comportamento padrão do SwipeableDrawer do MUI).
const TaskFilters = ({ searchParam, onSearchChange, filters, onFiltersChange, isMobile }) => {
  const classes = useStyles();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const setFilter = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const handleClearFilters = () => {
    onFiltersChange({
      status: "",
      priority: "",
      type: "",
      assignedUserId: null,
      contactId: null,
      dueDateFrom: "",
      dueDateTo: ""
    });
  };

  const searchField = (
    <TextField
      placeholder={i18n.t("tasks.searchPlaceholder")}
      type="search"
      variant="outlined"
      size="small"
      fullWidth={isMobile}
      value={searchParam}
      onChange={e => onSearchChange(e.target.value)}
      className={isMobile ? classes.fieldSpacing : undefined}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon color="secondary" />
          </InputAdornment>
        )
      }}
    />
  );

  const statusField = (
    <FormControl
      variant="outlined"
      size="small"
      fullWidth={isMobile}
      className={isMobile ? classes.fieldSpacing : undefined}
      style={!isMobile ? { minWidth: 140 } : undefined}
    >
      <InputLabel id="task-filter-status-label">{i18n.t("tasks.filters.status")}</InputLabel>
      <Select
        labelId="task-filter-status-label"
        label={i18n.t("tasks.filters.status")}
        value={filters.status || ""}
        onChange={e => setFilter("status", e.target.value)}
      >
        <MenuItem value="">{i18n.t("tasks.filters.all")}</MenuItem>
        {TASK_STATUS_OPTIONS.map(status => (
          <MenuItem key={status} value={status}>
            {i18n.t(`tasks.status.${status}`)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const priorityField = (
    <FormControl
      variant="outlined"
      size="small"
      fullWidth={isMobile}
      className={isMobile ? classes.fieldSpacing : undefined}
      style={!isMobile ? { minWidth: 140 } : undefined}
    >
      <InputLabel id="task-filter-priority-label">{i18n.t("tasks.filters.priority")}</InputLabel>
      <Select
        labelId="task-filter-priority-label"
        label={i18n.t("tasks.filters.priority")}
        value={filters.priority || ""}
        onChange={e => setFilter("priority", e.target.value)}
      >
        <MenuItem value="">{i18n.t("tasks.filters.all")}</MenuItem>
        {TASK_PRIORITY_OPTIONS.map(priority => (
          <MenuItem key={priority} value={priority}>
            {i18n.t(`tasks.priority.${priority}`)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const typeField = (
    <FormControl
      variant="outlined"
      size="small"
      fullWidth={isMobile}
      className={isMobile ? classes.fieldSpacing : undefined}
      style={!isMobile ? { minWidth: 140 } : undefined}
    >
      <InputLabel id="task-filter-type-label">{i18n.t("tasks.filters.type")}</InputLabel>
      <Select
        labelId="task-filter-type-label"
        label={i18n.t("tasks.filters.type")}
        value={filters.type || ""}
        onChange={e => setFilter("type", e.target.value)}
      >
        <MenuItem value="">{i18n.t("tasks.filters.all")}</MenuItem>
        {TASK_TYPE_OPTIONS.map(type => (
          <MenuItem key={type} value={type}>
            {i18n.t(`tasks.type.${type}`)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const assignedUserField = (
    <Box className={isMobile ? classes.fieldSpacing : undefined} style={!isMobile ? { minWidth: 200 } : undefined}>
      <TaskUserAutocomplete
        label={i18n.t("tasks.filters.assignedUser")}
        value={filters.assignedUserOption || null}
        onChange={newValue =>
          onFiltersChange({
            ...filters,
            assignedUserOption: newValue,
            assignedUserId: newValue ? newValue.id : null
          })
        }
      />
    </Box>
  );

  const contactField = (
    <Box className={isMobile ? classes.fieldSpacing : undefined} style={!isMobile ? { minWidth: 200 } : undefined}>
      <TaskContactAutocomplete
        label={i18n.t("tasks.filters.contact")}
        value={filters.contactOption || null}
        onChange={newValue =>
          onFiltersChange({
            ...filters,
            contactOption: newValue,
            contactId: newValue ? newValue.id : null
          })
        }
      />
    </Box>
  );

  const dueDateFromField = (
    <TextField
      type="date"
      label={i18n.t("tasks.filters.dueDateFrom")}
      variant="outlined"
      size="small"
      fullWidth={isMobile}
      className={isMobile ? classes.fieldSpacing : undefined}
      style={!isMobile ? { minWidth: 160 } : undefined}
      InputLabelProps={{ shrink: true }}
      value={filters.dueDateFrom || ""}
      onChange={e => setFilter("dueDateFrom", e.target.value)}
    />
  );

  const dueDateToField = (
    <TextField
      type="date"
      label={i18n.t("tasks.filters.dueDateTo")}
      variant="outlined"
      size="small"
      fullWidth={isMobile}
      className={isMobile ? classes.fieldSpacing : undefined}
      style={!isMobile ? { minWidth: 160 } : undefined}
      InputLabelProps={{ shrink: true }}
      value={filters.dueDateTo || ""}
      onChange={e => setFilter("dueDateTo", e.target.value)}
    />
  );

  if (isMobile) {
    return (
      <>
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setDrawerOpen(true)}
          fullWidth
        >
          {i18n.t("tasks.buttons.filters")}
        </Button>

        <SwipeableDrawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onOpen={() => setDrawerOpen(true)}
          disableSwipeToOpen
        >
          <Box className={classes.drawerContent} role="presentation">
            <Box className={classes.drawerHeader}>
              <Typography variant="h6">{i18n.t("tasks.filters.title")}</Typography>
              <IconButton
                aria-label={i18n.t("tasks.buttons.close")}
                onClick={() => setDrawerOpen(false)}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {searchField}
            {statusField}
            {priorityField}
            {typeField}
            {assignedUserField}
            {contactField}
            {dueDateFromField}
            {dueDateToField}

            <Box display="flex" justifyContent="space-between" style={{ gap: 8 }}>
              <Button onClick={handleClearFilters} color="secondary">
                {i18n.t("tasks.buttons.clear")}
              </Button>
              <Button
                onClick={() => setDrawerOpen(false)}
                color="primary"
                variant="contained"
              >
                {i18n.t("tasks.buttons.apply")}
              </Button>
            </Box>
          </Box>
        </SwipeableDrawer>
      </>
    );
  }

  return (
    <Grid container spacing={1} alignItems="center" className={classes.desktopFilters}>
      <Grid item>{searchField}</Grid>
      <Grid item>{statusField}</Grid>
      <Grid item>{priorityField}</Grid>
      <Grid item>{typeField}</Grid>
      <Grid item>{assignedUserField}</Grid>
      <Grid item>{contactField}</Grid>
      <Grid item>{dueDateFromField}</Grid>
      <Grid item>{dueDateToField}</Grid>
      <Grid item>
        <Button onClick={handleClearFilters} color="secondary" size="small">
          {i18n.t("tasks.buttons.clear")}
        </Button>
      </Grid>
    </Grid>
  );
};

export default TaskFilters;
